import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  Plus,
  UploadCloud,
  Eraser,
  LifeBuoy,
  School,
  Users,
} from "lucide-react";
import {
  customerInModalSelectStyles,
  formatCustomerOptionLabel,
  filterCustomerOption,
  mapBranchCustomersToOptions,
  modalSelectPortalProps,
} from "../../../components/ui/customerSelectInModal";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";
import { hasPermission } from "../../../utils/permissionUtils";

const TEXT = {
  searchPlaceholder: "Tìm trường, giáo viên, email...",
  refresh: "Làm mới",
  advanced: "Lọc nâng cao",
  advancedTitle: "Bộ lọc nâng cao",
  branch: "Chi nhánh",
  school: "Trường học",
  status: "Trạng thái",
  allBranch: "Tất cả chi nhánh",
  schoolPlaceholder: "Chọn trường",
  schoolNeedBranch: "Chọn chi nhánh trước",
  allStatus: "Tất cả trạng thái",
  clear: "Xóa lọc",
  apply: "ÁP DỤNG",
  addAccount: "Thêm tài khoản",
  importExcel: "Nhập excel",
};

const TeacherAccountFilter = ({
  filters,
  onChange,
  branchOptions = [],
  customerOptions = [],
  onNewClick,
  onImportClick,
  onRefresh,
  viewMode = "school",
  onViewModeChange = () => {},
}) => {
  const [advanced, setAdvanced] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const update = (name, value) => {
    if (name === "branch_id") {
      onChange({ ...filters, branch_id: value, customer_id: "" });
    } else {
      onChange({ ...filters, [name]: value });
    }
  };

  const clearFilters = () => {
    onChange({
      keyword: "",
      branch_id: "",
      customer_id: "",
      status: "",
      school_level: "",
    });
  };

  const branchSelectOptions = branchOptions.map((b) => ({
    value: b.id,
    label: b.name,
  }));

  const customerSelectOptions = mapBranchCustomersToOptions(customerOptions);

  const statusOptions = [
    { value: "active", label: "Đang hoạt động" },
    { value: "inactive", label: "Ngưng sử dụng" },
  ];

  const isFiltering = Boolean(
    filters.keyword?.trim() ||
      filters.branch_id ||
      filters.customer_id ||
      filters.status ||
      filters.school_level
  );

  const handleRefresh = () => {
    onRefresh?.();
  };

  const ActionButton = ({ icon: Icon, onClick, title, active = false }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 ${
        active
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
      }`}
    >
      <Icon size={18} />
    </button>
  );

  const selectStyles = {
    ...customSelectStyles,
    control: (provided, state) => ({
      ...(typeof customSelectStyles.control === "function"
        ? customSelectStyles.control(provided, state)
        : provided),
      minHeight: 48,
      borderRadius: 16,
      borderColor: state.isFocused ? "#ef4444" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(239, 68, 68, 0.05)" : "none",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  };

  const schoolSelectStyles = {
    ...customerInModalSelectStyles,
    control: (provided, state) => ({
      ...(typeof customerInModalSelectStyles.control === "function"
        ? customerInModalSelectStyles.control(provided, state)
        : provided),
      minHeight: 48,
      borderRadius: 16,
      borderColor: state.isFocused ? "#ef4444" : "#e5e7eb",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(239, 68, 68, 0.05)" : "none",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  };

  const SelectField = ({ label, options, value, onChange: onSelChange, placeholder }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <Select
        options={options}
        placeholder={placeholder}
        styles={selectStyles}
        value={options.find((x) => x.value === value) || null}
        onChange={(opt) => onSelChange(opt?.value || "")}
        isClearable
        {...modalSelectPortalProps}
      />
    </div>
  );

  const StatusSelectField = ({ label, value, onChange: onSelChange }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <Select
        options={statusOptions}
        placeholder={TEXT.allStatus}
        styles={selectStyles}
        value={statusOptions.find((x) => x.value === value) || null}
        onChange={(opt) => onSelChange(opt?.value || "")}
        isClearable
        {...modalSelectPortalProps}
      />
    </div>
  );

  const SchoolSelectField = ({ label, value, onChange: onSelChange, disabled }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1">
        {label}
      </label>
      <Select
        styles={schoolSelectStyles}
        isClearable
        isDisabled={disabled}
        placeholder={disabled ? TEXT.schoolNeedBranch : TEXT.schoolPlaceholder}
        options={customerSelectOptions}
        value={customerSelectOptions.find((o) => o.value === value) || null}
        onChange={(opt) => onSelChange(opt?.value || "")}
        formatOptionLabel={formatCustomerOptionLabel}
        filterOption={filterCustomerOption}
        {...modalSelectPortalProps}
      />
    </div>
  );

  if (isMobile) {
    return (
      <div className="mb-6 space-y-3">
        {/* Mobile View Mode Switcher */}
        <div className="grid grid-cols-2 rounded-2xl border border-gray-200 bg-gray-100/90 p-1">
          <button
            type="button"
            onClick={() => onViewModeChange?.("school")}
            className={`flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all ${
              viewMode === "school"
                ? "bg-white text-red-700 shadow-sm border border-gray-200/80"
                : "text-gray-500"
            }`}
          >
            <School size={15} />
            <span>Theo trường</span>
          </button>
          <button
            type="button"
            onClick={() => onViewModeChange?.("account")}
            className={`flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-all ${
              viewMode === "account"
                ? "bg-white text-red-700 shadow-sm border border-gray-200/80"
                : "text-gray-500"
            }`}
          >
            <Users size={15} />
            <span>Theo tài khoản</span>
          </button>
        </div>

        <div className="flex gap-2">
          <div className="relative flex-1 group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder={TEXT.searchPlaceholder}
              value={filters.keyword}
              onChange={(e) => update("keyword", e.target.value)}
              className="w-full bg-white border border-gray-200 pl-11 pr-4 py-3 rounded-2xl focus:outline-none focus:border-red-500 shadow-sm transition-all"
            />
          </div>
          <ActionButton icon={RotateCw} onClick={handleRefresh} title={TEXT.refresh} />
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(true)}
            title={TEXT.advanced}
            active={advanced}
          />
        </div>

        {hasPermission("crm.teacher_accounts", "C") && (
          <div className="fixed bottom-20 right-5 flex flex-col gap-3 z-40">
            <button
              type="button"
              onClick={() => onImportClick?.()}
              className="bg-white text-red-700 w-14 h-14 rounded-full shadow-lg shadow-red-100 text-xl flex items-center justify-center hover:scale-105 active:scale-95 border border-red-100"
              title={TEXT.importExcel}
            >
              <UploadCloud size={22} />
            </button>
            <button
              type="button"
              onClick={onNewClick}
              className="bg-gradient-to-r from-red-700 to-red-800 text-white w-14 h-14 rounded-full shadow-lg shadow-red-200 text-xl flex items-center justify-center hover:scale-105 active:scale-95"
              title={TEXT.addAccount}
            >
              <Plus size={22} />
            </button>
          </div>
        )}

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title={TEXT.advancedTitle}
          footer={
            <>
              {isFiltering ? (
                <button
                  type="button"
                  onClick={() => {
                    clearFilters();
                    setAdvanced(false);
                  }}
                  className="flex-1 py-4 border border-gray-300 text-gray-600 rounded-2xl font-bold hover:bg-gray-50"
                >
                  {TEXT.clear}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setAdvanced(false)}
                className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200"
              >
                {TEXT.apply}
              </button>
            </>
          }
        >
          <div className="grid grid-cols-1 gap-4">
            <SelectField
              label={TEXT.branch}
              options={branchSelectOptions}
              value={filters.branch_id}
              onChange={(v) => update("branch_id", v)}
              placeholder={TEXT.allBranch}
            />
            <SchoolSelectField
              label={TEXT.school}
              value={filters.customer_id}
              onChange={(v) => update("customer_id", v)}
              disabled={!filters.branch_id}
            />
            <StatusSelectField
              label={TEXT.status}
              value={filters.status}
              onChange={(v) => update("status", v)}
            />
          </div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  return (
    <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm mb-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0 flex-wrap">
          <div className="relative w-full max-w-sm group">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors"
              size={18}
            />
            <input
              type="text"
              placeholder={TEXT.searchPlaceholder}
              value={filters.keyword}
              onChange={(e) => update("keyword", e.target.value)}
              className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton icon={RotateCw} onClick={handleRefresh} title={TEXT.refresh} />
          <ActionButton
            icon={SlidersHorizontal}
            onClick={() => setAdvanced(!advanced)}
            active={advanced}
            title={TEXT.advanced}
          />

          {/* Segmented View Mode Toggle */}
          <div className="flex items-center rounded-2xl border border-gray-200 bg-gray-50 p-1 shrink-0">
            <button
              type="button"
              onClick={() => onViewModeChange?.("school")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                viewMode === "school"
                  ? "bg-white text-red-700 shadow-sm border border-gray-200/80"
                  : "text-gray-500 hover:text-gray-800"
              }`}
              title="Chế độ xem theo danh sách trường học"
            >
              <School size={15} />
              <span className="hidden sm:inline">Theo trường</span>
            </button>
            <button
              type="button"
              onClick={() => onViewModeChange?.("account")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                viewMode === "account"
                  ? "bg-white text-red-700 shadow-sm border border-gray-200/80"
                  : "text-gray-500 hover:text-gray-800"
              }`}
              title="Chế độ xem theo danh sách tài khoản (nhóm theo trường)"
            >
              <Users size={15} />
              <span className="hidden sm:inline">Theo tài khoản</span>
            </button>
          </div>

          {isFiltering && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-1 shrink-0"
            >
              <Eraser size={14} /> {TEXT.clear}
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap justify-end">
          <Link
            to="/crm/teacher_support"
            className="flex items-center gap-1.5 px-4 py-2.5 bg-gray-50 text-gray-700 hover:text-red-700 rounded-full font-bold border border-gray-200 hover:border-red-200 transition-all active:scale-95 text-xs"
            title="Xem các góp ý & yêu cầu hỗ trợ từ giáo viên đối tác"
          >
            <LifeBuoy size={16} className="text-red-600" />
            Hỗ trợ & Góp ý
          </Link>
          {hasPermission("crm.teacher_accounts", "C") && (
            <>
              <button
                type="button"
                onClick={() => onImportClick?.()}
                className="flex items-center gap-2 px-5 py-2.5 bg-white text-red-700 rounded-full font-bold border border-red-100 hover:shadow-lg hover:shadow-red-50 transition-all active:scale-95 uppercase text-xs tracking-wider"
              >
                <UploadCloud size={18} />
                {TEXT.importExcel}
              </button>
              <button
                type="button"
                onClick={onNewClick}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-full font-bold hover:shadow-lg hover:shadow-red-100 transition-all active:scale-95 uppercase text-xs tracking-wider"
              >
                <Plus size={18} />
                {TEXT.addAccount}
              </button>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
              <SelectField
                label={TEXT.branch}
                options={branchSelectOptions}
                value={filters.branch_id}
                onChange={(v) => update("branch_id", v)}
                placeholder={TEXT.allBranch}
              />
              <SchoolSelectField
                label={TEXT.school}
                value={filters.customer_id}
                onChange={(v) => update("customer_id", v)}
                disabled={!filters.branch_id}
              />
              <StatusSelectField
                label={TEXT.status}
                value={filters.status}
                onChange={(v) => update("status", v)}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TeacherAccountFilter;
